import type { FetchResult } from '@apollo/client'
import type { Blog, BlogCategory } from '@ztechapps/common.contract'
import { useError, useMutation } from '@ztechapps/common.hook'
import { blogCategoryQueries } from '../../BlogCategory/graphql'
import { treeBlogQueries } from './graphql'

type CreateBlogCategoryInput = Pick<BlogCategory, 'name' | 'parent'>
type UpdateBlogCategoryInput = Pick<BlogCategory, '_id'> & Partial<Pick<BlogCategory, 'name' | 'parent'>>
type UpdateBlogInput = Pick<Blog, '_id' | 'category'>

export type UseBlogCategoryTreeActionsResult = {
  moveBlog: (blogId: string, categoryId: string) => Promise<FetchResult>
  /** `parentId` null moves the category to the top level. */
  moveBlogCategory: (categoryId: string, parentId: string | null) => Promise<FetchResult>
  /** `parentId` null adds a top-level category. */
  addBlogCategory: (name: string, parentId: string | null) => Promise<FetchResult>
  renameBlogCategory: (categoryId: string, name: string) => Promise<FetchResult>
  deleteBlogCategory: (categoryId: string) => Promise<FetchResult>
}

// Each write refetches the lists the tree reads, so a created or deleted blog category, and every count, show at once.
export const useBlogCategoryTreeActions = (): UseBlogCategoryTreeActionsResult => {
  const [updateBlog, , updateBlogResult] = useMutation<Blog, UpdateBlogInput>({
    ...treeBlogQueries.updateConfig,
    options: { mutationOptions: { refetchQueries: ['allBlog'] } },
  })
  const [createBlogCategory, , createBlogCategoryResult] = useMutation<BlogCategory, CreateBlogCategoryInput>({
    ...blogCategoryQueries.createConfig,
    options: { mutationOptions: { refetchQueries: blogCategoryQueries.createConfig.refetchQueries } },
  })
  const [updateBlogCategory, , updateBlogCategoryResult] = useMutation<BlogCategory, UpdateBlogCategoryInput>({
    ...blogCategoryQueries.updateConfig,
    options: { mutationOptions: { refetchQueries: ['allBlogCategory'] } },
  })
  const [deleteBlogCategory, , deleteBlogCategoryResult] = useMutation<BlogCategory>({
    ...blogCategoryQueries.deleteConfig,
    options: { mutationOptions: { refetchQueries: blogCategoryQueries.deleteConfig.refetchQueries } },
  })
  useError(updateBlogResult.error)
  useError(createBlogCategoryResult.error)
  useError(updateBlogCategoryResult.error)
  useError(deleteBlogCategoryResult.error)

  return {
    moveBlog: (blogId, categoryId) => updateBlog({ input: { _id: blogId, category: { _id: categoryId } } }),
    moveBlogCategory: (categoryId, parentId) =>
      updateBlogCategory({ input: { _id: categoryId, parent: parentId ? { _id: parentId } : null } }),
    addBlogCategory: (name, parentId) =>
      createBlogCategory({ input: { name, parent: parentId ? { _id: parentId } : null } }),
    renameBlogCategory: (categoryId, name) => updateBlogCategory({ input: { _id: categoryId, name } }),
    deleteBlogCategory: (categoryId) => deleteBlogCategory({ filter: { _id: categoryId } }),
  }
}
